/**
 * bubu-secciones.js — comportamiento común de las secciones editoriales:
 *  - Carriles horizontales: [data-bubu-carril] con flechas [data-bubu-carril-prev/next]
 *    (scroll controlado, sin scrollIntoView).
 *  - Vídeos de clientas: todos quietos con su botón de play, uno solo en marcha, y el
 *    primero arranca solo la primera vez para enseñar que se pueden reproducir.
 *  - Los carriles se arrastran también con el ratón. Ninguno se mueve solo: el de
 *    vídeos lleva puntos de página debajo para que se vea cuántos hay.
 */

function carrilDe(btn) {
  const raiz = btn.closest('[data-bubu-upsell], .bubu-videos, [data-bubu-carril-raiz]') || btn.parentElement.parentElement;
  return raiz ? raiz.querySelector('[data-bubu-carril]') : null;
}

// Mismo paso que el diseño: el 82 % del ancho visible, con un mínimo de 220px. Antes se
// calculaba por número de tarjetas enteras y en un carril de miniaturas de 68px eso
// saltaba media pantalla o no llegaba a moverse. El suavizado lo pone el CSS del carril
// (scroll-behavior:smooth), aquí solo se cambia scrollLeft.
// Vive en una función porque el carril en bucle necesita saber cuánto va a moverse ANTES
// de que se mueva, para dejar sitio por delante. Dos copias de esta cuenta acabarían
// separándose y el bucle empezaría a dar tirones.
function pasoCarril(c) {
  return Math.max(220, Math.round(c.clientWidth * 0.82));
}

document.addEventListener('click', (e) => {
  const prev = e.target.closest('[data-bubu-carril-prev]');
  const next = e.target.closest('[data-bubu-carril-next]');
  if (!prev && !next) return;
  const c = carrilDe(prev || next);
  if (!c) return;
  c.scrollLeft = c.scrollLeft + (next ? 1 : -1) * pasoCarril(c);
});

/* ---------------------------------------------------------------------------
   Carriles que hay que ENTENDER que se deslizan. Las pestañas de la tienda
   («Todos · Novedades · Bestsellers · Mystery · Extras») y los chips de los
   documentos legales se cortaban a hueso contra el borde: quien no probara a
   arrastrar no llegaba a saber que había más secciones. Aquí se les añaden las
   tres señales del resto de la web:
     · desvanecido en el lado por el que queda contenido (clases is-inicio/is-fin),
     · una flecha pulsable a cada lado, que solo existe si de verdad hay desborde,
     · y, al cargar, la pestaña activa se coloca a la vista.
   Sin scrollIntoView: se escribe scrollLeft y ya.
   --------------------------------------------------------------------------- */
const FLECHA_SVG =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="m9 5 7 7-7 7"/></svg>';

function avisaDesliz(carril) {
  if (!carril || carril._bubuDesliz) return;
  carril._bubuDesliz = true;
  const marco = carril.parentElement;
  // Si el marco ya trae sus flechas (los chips legales), se reutilizan: lo único que
  // falta ahí es esconderlas cuando no hacen falta.
  let prev = marco ? marco.querySelector(':scope > [data-bubu-carril-prev]') : null;
  let next = marco ? marco.querySelector(':scope > [data-bubu-carril-next]') : null;
  if (marco && !prev && !next) {
    // Las flechas van pegadas al carril, no al bloque de controles (que además lleva
    // la fila de «6 slimes · Filtrar · Destacados» debajo y las dejaría a media
    // altura). Se le pone un envoltorio propio.
    const caja = document.createElement('div');
    caja.className = 'bubu-desliz';
    carril.parentNode.insertBefore(caja, carril);
    caja.appendChild(carril);
    const crea = (lado) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'bubu-desliz__flecha bubu-desliz__flecha--' + lado;
      b.setAttribute('aria-label', lado === 'prev' ? 'Ver las secciones anteriores' : 'Ver más secciones');
      b.innerHTML = FLECHA_SVG;
      b.addEventListener('click', () => {
        const paso = Math.max(160, Math.round(carril.clientWidth * 0.7));
        carril.scrollLeft += (lado === 'next' ? 1 : -1) * paso;
      });
      caja.appendChild(b);
      return b;
    };
    prev = crea('prev');
    next = crea('next');
  }
  let pendiente = false;
  const mide = () => {
    pendiente = false;
    const desborda = carril.scrollWidth > carril.clientWidth + 2;
    const inicio = !desborda || carril.scrollLeft <= 2;
    const fin = !desborda || carril.scrollLeft >= carril.scrollWidth - carril.clientWidth - 2;
    carril.classList.toggle('is-inicio', inicio);
    carril.classList.toggle('is-fin', fin);
    if (prev) prev.classList.toggle('is-sin-uso', inicio);
    if (next) next.classList.toggle('is-sin-uso', fin);
  };
  const pide = () => {
    if (pendiente) return;
    pendiente = true;
    requestAnimationFrame(mide);
  };
  carril.addEventListener('scroll', pide, { passive: true });
  window.addEventListener('resize', pide);
  // La pestaña activa, a la vista. Si es la quinta, entrar y no verla equivale a no
  // saber en qué sección estás.
  const activo = carril.querySelector('.is-activa, .is-activo, [aria-current="page"]');
  if (activo) {
    const d = activo.offsetLeft - carril.offsetLeft - 16;
    if (d > 0) carril.scrollLeft = d;
  }
  mide();
}

document.querySelectorAll('.bubu-carril-pestanas, .bubu-chips__carril').forEach(avisaDesliz);
window.bubuAvisaDesliz = avisaDesliz;

// ---------- Vídeos de clientas ----------
/* Reglas de la casa, y son las tres a la vez:
     1. TODOS quietos. Un vídeo solo se enciende si alguien le da al play. Ocho vídeos
        verticales descodificando a la vez era lo que hacía que la home fuera a tirones.
     2. Uno, y solo uno, arranca solo la primera vez que el carril se ve: sin eso nadie
        descubre que las tarjetas se pueden reproducir. Suena a nada —todas las reseñas
        son mudas—, se ve entera y se para. A partir de ahí, manda el dedo.
     3. Como mucho hay UNO en marcha. Dar al play en otro para el anterior.
   Lo que NO hace: moverse solo. El carril que se va justo cuando vas a pulsar molesta
   más de lo que enseña, y encima compite con el vídeo que está puesto. Que hay más se
   cuenta con los puntos de debajo, igual que en la galería de la ficha. */
const videos = Array.from(document.querySelectorAll('[data-bubu-video]'));

function tarjetaDe(v) {
  return v.closest('.bubu-video');
}

function cargaVideo(v) {
  if (v.dataset.src && !v.src) {
    // `preload=metadata` + `#t=0.001`: el navegador se baja solo la cabecera y pinta el
    // PRIMER FOTOGRAMA, que hace de póster. Sin esto el hueco se quedaba en negro hasta
    // que descargaba el vídeo entero, porque estos vídeos se suben como archivo y no
    // traen imagen de portada.
    v.preload = 'metadata';
    v.src = v.dataset.src + (v.dataset.src.indexOf('#') === -1 ? '#t=0.001' : '');
    v.addEventListener('loadeddata', () => tarjetaDe(v)?.classList.add('is-listo'), { once: true });
  }
}

if (videos.length) {
  let enMarcha = null;

  const marca = (v, on) => {
    const t = tarjetaDe(v);
    if (t) t.classList.toggle('is-jugando', on);
    const btn = t && t.querySelector('[data-bubu-play]');
    if (btn) btn.setAttribute('aria-label', on ? 'Pausar el vídeo' : 'Reproducir el vídeo');
  };

  const para = (v) => {
    if (!v) return;
    v.pause();
    marca(v, false);
    if (enMarcha === v) enMarcha = null;
  };

  const arranca = (v) => {
    if (!v || enMarcha === v) return;
    para(enMarcha);
    cargaVideo(v);
    v.muted = true; // las reseñas no llevan audio: nunca se pide sonido
    const p = v.play();
    if (p && p.catch) p.catch(() => marca(v, false));
    enMarcha = v;
    marca(v, true);
  };

  videos.forEach((v) => {
    v.addEventListener('ended', () => para(v));
  });

  // Un clic en cualquier parte de la tarjeta enciende o apaga: el botón es la señal,
  // pero el área pulsable es la tarjeta entera, que es lo que el dedo busca.
  document.addEventListener('click', (e) => {
    const caja = e.target.closest('.bubu-video');
    if (!caja || caja._bubuArrastrado) return;
    const v = caja.querySelector('[data-bubu-video]');
    if (!v) return;
    if (enMarcha === v) para(v);
    else arranca(v);
  });

  if ('IntersectionObserver' in window) {
    // Primer aro, generoso: solo prepara la descarga de los que vienen de camino.
    const ioCarga = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          cargaVideo(en.target);
          ioCarga.unobserve(en.target);
        });
      },
      { rootMargin: '300px' }
    );
    videos.forEach((v) => ioCarga.observe(v));

    // Lo que se va de la pantalla se para: seguir descodificando algo que nadie ve es
    // gastar batería por gusto.
    const ioVista = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting && enMarcha === en.target) para(en.target);
        });
      },
      { threshold: 0.25 }
    );
    videos.forEach((v) => ioVista.observe(v));

    // El primero de cada carril, una vez, cuando el carril aparece.
    document.querySelectorAll('.bubu-videos').forEach((sec) => {
      const primero = sec.querySelector('[data-bubu-video]');
      if (!primero) return;
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((en) => {
            if (!en.isIntersecting) return;
            io.disconnect();
            if (!enMarcha) arranca(primero);
          });
        },
        { threshold: 0.4 }
      );
      io.observe(sec);
    });
  }
}

/* ---------- Carriles en bucle ----------
   Un carril con `data-bubu-carril-bucle` no tiene final: al llegar al último vuelve a
   aparecer el primero, así que parece que siempre hay más. Se usa donde el orden da
   igual y la gracia es pasear —«Quién vive en cada zona»—, nunca donde el final es
   información («ya los has visto todos»).

   Cómo funciona, porque el truco importa: no se mueve nada con transform ni se
   reordenan nodos al vuelo (las dos cosas pelean con el scroll nativo y se notan en el
   dedo). Se clonan juegos enteros de tarjetas DELANTE y DETRÁS de las de verdad, y
   cuando el scroll se sale de la tanda buena se le suma o se le resta el ancho de una
   vuelta. Como lo que hay a un lado y al otro es idéntico, el salto es invisible: la
   pantalla enseña exactamente lo mismo antes y después.

   Cuántos juegos: los que hagan falta para que a cada lado quepa una pantalla entera.
   Con pocas tarjetas un solo juego no llenaría el hueco y se vería el vacío al saltar.

   Los clones son decorado: van con aria-hidden y sin tabulación, para que un lector de
   pantalla lea los personajes una vez y el tabulador no dé tres vueltas al mismo sitio.
   Con el ratón siguen siendo enlaces normales, que es lo que espera quien pulsa. */
document.querySelectorAll('[data-bubu-carril-bucle]').forEach((carril) => {
  const originales = Array.from(carril.children);
  if (originales.length < 2) return;
  // Si no desborda no hay nada que dar la vuelta: clonar aquí solo añadiría peso.
  if (carril.scrollWidth <= carril.clientWidth + 4) return;

  const juego = (delante) => {
    const trozo = document.createDocumentFragment();
    originales.forEach((el) => {
      const c = el.cloneNode(true);
      c.setAttribute('aria-hidden', 'true');
      c.setAttribute('data-bubu-clon', '');
      if (c.hasAttribute('href') || c.tagName === 'BUTTON') c.tabIndex = -1;
      c.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((n) => (n.tabIndex = -1));
      trozo.appendChild(c);
    });
    if (delante) carril.insertBefore(trozo, carril.firstChild);
    else carril.appendChild(trozo);
  };

  // Primer juego detrás: es el que permite medir la vuelta sin hacer cuentas con
  // huecos ni rellenos. La distancia entre la primera tarjeta y su copia ES el ancho
  // de una vuelta, al píxel.
  juego(false);
  const vuelta = carril.children[originales.length].offsetLeft - originales[0].offsetLeft;
  if (!(vuelta > 0)) return;

  // Juegos a cada lado: los que hagan falta para que quepa una pantalla entera Y el
  // salto de una flecha. Con menos, una flecha pedía llegar más allá del último clon,
  // el navegador se quedaba clavado en el tope y el carril dejaba de avanzar.
  const juegos = Math.max(1, Math.ceil(Math.max(carril.clientWidth, pasoCarril(carril)) / vuelta));
  for (let i = 1; i < juegos; i++) juego(false);
  for (let i = 0; i < juegos; i++) juego(true);

  const base = juegos * vuelta;
  const sitio = (x) => {
    const antes = carril.style.scrollBehavior;
    carril.style.scrollBehavior = 'auto';
    carril.scrollLeft = x;
    carril.style.scrollBehavior = antes;
  };
  sitio(base);

  // Mientras una flecha está llevando el carril de un sitio a otro (scroll suave del
  // CSS) no se toca nada: cambiar scrollLeft a media animación la corta en seco. El
  // ajuste se hace ANTES de empezar el viaje, en `normaliza`.
  let hasta = 0;
  const normaliza = () => {
    let x = carril.scrollLeft;
    while (x >= base + vuelta) x -= vuelta;
    while (x < base - 0.5) x += vuelta;
    if (Math.abs(x - carril.scrollLeft) > 0.5) sitio(x);
    return x;
  };
  // Antes de que una flecha empuje: si el viaje se saldría del contenido, se retrocede
  // una vuelta entera. Se ve exactamente lo mismo —lo de atrás es idéntico a lo de
  // delante— pero ahora sí hay pista por delante para el salto.
  const sitioParaElPaso = (dir) => {
    const tope = carril.scrollWidth - carril.clientWidth;
    const paso = pasoCarril(carril) * dir;
    let x = normaliza();
    if (x + paso > tope) sitio(x - vuelta);
    else if (x + paso < 0) sitio(x + vuelta);
  };
  let pendiente = false;
  carril.addEventListener(
    'scroll',
    () => {
      if (pendiente || Date.now() < hasta) return;
      pendiente = true;
      requestAnimationFrame(() => {
        pendiente = false;
        normaliza();
      });
    },
    { passive: true }
  );
  const marco = carril.closest('.bubu-carrusel__marco, .bubu-videos__marco') || carril.parentElement;
  if (marco) {
    marco.addEventListener(
      'click',
      (e) => {
        const b = e.target.closest('[data-bubu-carril-prev], [data-bubu-carril-next]');
        if (!b) return;
        sitioParaElPaso(b.hasAttribute('data-bubu-carril-next') ? 1 : -1);
        // Mientras el scroll suave está de viaje no se toca nada: cambiar scrollLeft a
        // media animación la corta en seco. Al llegar se recoloca, porque si nadie
        // vuelve a tocar el carril no habrá otro evento de scroll que lo haga.
        hasta = Date.now() + 700;
        setTimeout(normaliza, 760);
      },
      { capture: true }
    );
  }
});

/* ---------- Puntos de página ----------
   Los mismos puntos que la galería de la ficha, pero por PÁGINAS, no por tarjetas: con
   doce vídeos, doce puntos no dicen nada; tres o cuatro sí dicen «hay más de lo que ves
   y esto se desliza». Se pintan solos a partir de lo que mide el carril, así que subir
   o quitar vídeos en el editor no obliga a tocar nada, y si todo cabe en pantalla la
   fila de mandos entera desaparece: unos puntos que no llevan a ningún sitio son ruido.
   Van DEBAJO del carril, nunca encima: sobre las tarjetas tapaban el botón de play. */
document.querySelectorAll('[data-bubu-puntos]').forEach((caja) => {
  const raiz = caja.closest('[data-bubu-carril-raiz]') || caja.parentElement;
  const carril = raiz && raiz.querySelector('[data-bubu-carril]');
  if (!carril) return;
  const mandos = caja.closest('[data-bubu-mandos]') || caja;
  let botones = [];

  // Media página suelta al final sigue siendo una página: si no se cuenta, el último
  // vídeo no tiene punto al que ir.
  const cuantas = () => Math.max(1, Math.round((carril.scrollWidth - 2) / Math.max(1, carril.clientWidth) + 0.35));

  const construye = (n) => {
    caja.textContent = '';
    botones = [];
    for (let k = 0; k < n; k++) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'bubu-puntos__p';
      b.setAttribute('aria-label', 'Ir al grupo ' + (k + 1) + ' de ' + n);
      b.addEventListener('click', () => {
        // La última página casi nunca es una página entera: se va al tope, no a un
        // sitio que no existe, o el carril se queda a medias y el punto no se enciende.
        carril.scrollLeft = Math.min(k * carril.clientWidth, carril.scrollWidth - carril.clientWidth);
      });
      caja.appendChild(b);
      botones.push(b);
    }
  };

  const pinta = () => {
    const n = cuantas();
    if (n !== botones.length) construye(n);
    mandos.hidden = n < 2;
    // Del final se sale por arriba: el navegador no deja pasar del tope, así que ahí
    // manda el tope y no la división, o el último punto no se encendería jamás.
    const tope = carril.scrollWidth - carril.clientWidth;
    const i =
      tope > 0 && carril.scrollLeft >= tope - 2
        ? n - 1
        : Math.max(0, Math.min(n - 1, Math.round(carril.scrollLeft / Math.max(1, carril.clientWidth))));
    botones.forEach((b, k) => {
      const on = k === i;
      b.classList.toggle('is-activo', on);
      if (on) b.setAttribute('aria-current', 'true');
      else b.removeAttribute('aria-current');
    });
  };

  let pendiente = false;
  const pide = () => {
    if (pendiente) return;
    pendiente = true;
    requestAnimationFrame(() => {
      pendiente = false;
      pinta();
    });
  };
  carril.addEventListener('scroll', pide, { passive: true });
  window.addEventListener('resize', pide);
  // Los vídeos llegan tarde y pueden cambiar el ancho del carril: se vuelve a medir
  // cuando cambia de tamaño de verdad, no a ojo con un temporizador.
  if ('ResizeObserver' in window) new ResizeObserver(pide).observe(carril);
  pinta();
});

/* ---------- Arrastrar con el ratón ----------
   Con el dedo, un carril ya se arrastra solo (scroll nativo, con su inercia). Con el
   ratón no: o flechas o rueda. Esto le da el mismo gesto al ratón —agarrar y tirar— sin
   tocar el táctil, que no necesita ayuda y encima se estropearía. Si el puntero se ha
   movido de verdad (más de 6 px) se anula el clic siguiente, para que arrastrar sobre
   una tarjeta no acabe abriendo el producto o encendiendo un vídeo. */
document.querySelectorAll('[data-bubu-carril]').forEach((carril) => {
  let activo = false;
  let x0 = 0;
  let s0 = 0;
  let movido = false;
  carril.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    activo = true;
    movido = false;
    x0 = e.clientX;
    s0 = carril.scrollLeft;
  });
  carril.addEventListener('pointermove', (e) => {
    if (!activo) return;
    const d = e.clientX - x0;
    if (!movido && Math.abs(d) < 6) return;
    if (!movido) {
      movido = true;
      carril.classList.add('is-arrastrando');
      // Mientras se arrastra manda el JS: con scroll-behavior:smooth cada paso se
      // animaba y el carril iba por detrás del ratón.
      carril.style.scrollBehavior = 'auto';
      carril.setPointerCapture(e.pointerId);
    }
    carril.scrollLeft = s0 - d;
  });
  const suelta = () => {
    if (!activo) return;
    activo = false;
    carril.classList.remove('is-arrastrando');
    carril.style.scrollBehavior = '';
    if (movido) {
      // El clic que viene detrás del arrastre no cuenta.
      carril.querySelectorAll('.bubu-video').forEach((t) => {
        t._bubuArrastrado = true;
        setTimeout(() => (t._bubuArrastrado = false), 0);
      });
      carril.addEventListener('click', (e) => e.stopPropagation(), { capture: true, once: true });
    }
  };
  carril.addEventListener('pointerup', suelta);
  carril.addEventListener('pointercancel', suelta);
  carril.addEventListener('dragstart', (e) => {
    if (movido) e.preventDefault();
  });
});

// ---------- Newsletter (Klaviyo Client API) ----------
document.querySelectorAll('[data-bubu-news][data-klaviyo-company]').forEach((sec) => {
  const form = sec.querySelector('[data-bubu-news-form]');
  if (!form) return;
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const input = form.querySelector('input[type="email"]');
    const btn = form.querySelector('button[type="submit"]');
    const txt = form.querySelector('[data-bubu-news-txt]');
    const ok = form.querySelector('[data-bubu-news-ok]');
    const email = (input.value || '').trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      input.focus();
      input.setAttribute('aria-invalid', 'true');
      return;
    }
    input.removeAttribute('aria-invalid');
    btn.disabled = true;
    const antes = txt ? txt.textContent : '';
    if (txt) txt.textContent = 'Un momento…';
    try {
      const r = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(sec.dataset.klaviyoCompany)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Web · Slime gratis',
              profile: { data: { type: 'profile', attributes: { email } } },
            },
            relationships: { list: { data: { type: 'list', id: sec.dataset.klaviyoLista } } },
          },
        }),
      });
      if (!r.ok && r.status !== 202) throw new Error('klaviyo ' + r.status);
      try { window.klaviyo && window.klaviyo.identify({ email: email }); } catch (e) {}
      input.value = '';
      if (ok) ok.hidden = false;
      if (txt) txt.textContent = '¡Listo!';
      setTimeout(() => { if (txt) txt.textContent = antes; btn.disabled = false; }, 4000);
    } catch (err) {
      if (txt) txt.textContent = antes;
      btn.disabled = false;
      document.dispatchEvent(new CustomEvent('bubu:cart-error', { detail: { mensaje: 'No hemos podido apuntarte. Inténtalo otra vez en un momento.' } }));
    }
  });
});

// ---------- Reseñas manuales: «Ver las N reseñas» en móvil ----------
document.addEventListener('click', (e) => {
  const b = e.target.closest('[data-bubu-resenas-mas]');
  if (!b) return;
  const g = b.closest('.bubu-resenas').querySelector('[data-bubu-resenas-grid]');
  if (g) g.classList.remove('is-plegado');
});

// ---------- Reseñas de Judge.me («todas»): «Ver más reseñas» ----------
// El bloque arranca recogido (CSS: is-plegado) y este botón lo despliega. El botón
// solo tiene sentido cuando Judge.me ya ha pintado reseñas dentro: hasta entonces se
// marca is-vacia y el CSS lo esconde.
document.querySelectorAll('[data-bubu-resenas-todas]').forEach((caja) => {
  const revisa = () => caja.classList.toggle('is-vacia', !caja.querySelector('.jdgm-rev'));
  revisa();
  new MutationObserver(revisa).observe(caja, { childList: true, subtree: true });
});
document.addEventListener('click', (e) => {
  const b = e.target.closest('[data-bubu-resenas-todas-mas]');
  if (!b) return;
  const caja = b.closest('.bubu-resenas').querySelector('[data-bubu-resenas-todas]');
  if (caja) caja.classList.remove('is-plegado');
});

// ---------- Mapa de Bubu World: al abrir una zona, se marca su punto ----------
document.querySelectorAll('[data-bubu-mapa]').forEach((mapa) => {
  const zonas = Array.from(mapa.querySelectorAll('[data-bubu-mapa-zona]'));
  zonas.forEach((z) => {
    z.addEventListener('toggle', () => {
      if (z.open) zonas.forEach((o) => { if (o !== z) o.open = false; });
      mapa.querySelectorAll('[data-bubu-mapa-punto]').forEach((p) => {
        p.classList.toggle('is-activo', z.open && p.dataset.bubuMapaPunto === z.dataset.bubuMapaZona);
      });
    });
  });
});

// ---------- Contacto: el botón se activa cuando el formulario está completo ----------
document.querySelectorAll('#bubu-contacto-form').forEach((f) => {
  const btn = f.querySelector('[data-bubu-contacto-btn]');
  if (!btn) return;
  const comprueba = () => {
    const ok = f.checkValidity();
    btn.disabled = !ok;
    btn.setAttribute('aria-disabled', ok ? 'false' : 'true');
  };
  f.addEventListener('input', comprueba);
  comprueba();
});

// ---------- Anillo de luz al añadir ----------
// El prototipo enciende una banda de luz alrededor de la tarjeta (o de la
// miniatura) recién añadida durante 1,4 s. Vive aquí para no tocar las dos
// animaciones que ya centraliza bubu-cart.js: es un adorno de la tarjeta, no
// del botón.
function anillo(origen) {
  const tarjeta = origen.closest('[data-bubu-tarjeta], .bubu-mini, .bubu-extra');
  if (!tarjeta) return;
  // En las miniaturas el anillo rodea la foto, que es la que lleva el radio.
  const caja = tarjeta.querySelector(':scope > .bubu-mini__foto, :scope > .bubu-extra__foto') || tarjeta;
  if (!caja.querySelector(':scope > .bubu-anillo')) {
    const s = document.createElement('span');
    s.className = 'bubu-anillo';
    s.setAttribute('aria-hidden', 'true');
    caja.appendChild(s);
  }
  caja.classList.remove('is-glow');
  void caja.offsetWidth;
  caja.classList.add('is-glow');
  clearTimeout(caja._bubuGlow);
  caja._bubuGlow = setTimeout(() => caja.classList.remove('is-glow'), 1400);
}

document.addEventListener('click', (e) => {
  const b = e.target.closest('[data-bubu-anadir],[data-bubu-mas],[data-bubu-add],[data-bubu-anadir-pack],[data-bubu-mas-uno]');
  if (b && !b.disabled) anillo(b);
});

/* ---------------------------------------------------------------------------
   Navegación instantánea. Cada salto de página es una carga completa de Shopify
   (~1 s). Aquí se adelanta: en cuanto el ratón se posa sobre un enlace interno
   —o el dedo lo toca— el navegador se descarga la página de fondo, así que al
   pulsar ya la tiene. Solo enlaces del propio dominio, nada de carrito, cuenta
   ni checkout, y con tope para no gastar datos de más.
   --------------------------------------------------------------------------- */
const yaPrecargado = new Set();
const TOPE_PRECARGA = 12;
function precarga(url) {
  if (yaPrecargado.size >= TOPE_PRECARGA || yaPrecargado.has(url)) return;
  yaPrecargado.add(url);
  const l = document.createElement('link');
  l.rel = 'prefetch';
  l.as = 'document';
  l.href = url;
  document.head.appendChild(l);
}
function candidata(e) {
  const a = e.target.closest && e.target.closest('a[href]');
  if (!a || a.target === '_blank' || a.hasAttribute('download')) return;
  let u;
  try {
    u = new URL(a.href, location.href);
  } catch (_) {
    return;
  }
  if (u.origin !== location.origin) return;
  if (u.pathname === location.pathname) return;
  if (/^\/(cart|checkout|account|apps|tools)\b/.test(u.pathname)) return;
  precarga(u.href);
}
if (!matchMedia('(prefers-reduced-data: reduce)').matches) {
  document.addEventListener('pointerover', candidata, { passive: true });
  document.addEventListener('touchstart', candidata, { passive: true });
}

/* ---------------------------------------------------------------------------
   DOS COSAS QUE LE FALTABAN A LOS CARRILES.

   1) QUE SE VEA QUE SE DESLIZA.
   En la caja de «El tercero mejora toda la caja» de la ficha, según el ancho de pantalla
   las miniaturas pueden caber justas y no quedar ninguna cortada. Entonces parece una
   fila fija y nadie prueba a arrastrar: los Bubus de la derecha no existen para el
   cliente. En cuanto el carril entra en pantalla por primera vez se mueve 26 px y vuelve.
   Una vez, no en bucle: es un aviso, no un adorno. Con `prefers-reduced-motion` no se
   hace, y tampoco si no hay nada a la derecha que enseñar.

   2) QUE NO HAYA HUECOS AL PASARLO A TODA PRISA.
   Las fotos y las portadas de los vídeos van en diferido (`loading="lazy"`), que es lo
   correcto para la carga inicial: si no, la home se traería veinte fotos que a lo mejor
   nadie mira. El problema es que el navegador las pide cuando ya casi se ven, y pasando
   el dedo rápido llegas antes tú que la descarga: tarjetas en blanco. En cuanto tocas un
   carril se deja de dudar y se piden TODAS las suyas de golpe (fotos y portadas de
   vídeo). No cuesta nada en la carga inicial —no pasa hasta que hay un dedo encima— y a
   partir de ahí ya no hay hueco posible por rápido que se pase.
   --------------------------------------------------------------------------- */
function despiertaFotos(carril) {
  if (carril._bubuFotos) return;
  carril._bubuFotos = true;
  carril.querySelectorAll('img[loading="lazy"]').forEach((img) => {
    img.loading = 'eager';
  });
  // Las portadas de <video> no tienen «lazy» que quitar: se piden aparte y el navegador
  // las guarda en caché, así que cuando el vídeo entre en pantalla ya estará puesta.
  carril.querySelectorAll('video[poster]').forEach((v) => {
    const im = new Image();
    im.decoding = 'async';
    im.src = v.getAttribute('poster');
  });
}

document.querySelectorAll('[data-bubu-carril]').forEach((carril) => {
  ['pointerdown', 'touchstart', 'wheel'].forEach((ev) =>
    carril.addEventListener(ev, () => despiertaFotos(carril), { passive: true, once: true })
  );
  // Arrastrar con las flechas también cuenta como «va a mirar»: el carril se desplaza
  // solo y las fotos tienen que estar.
  const raiz = carril.closest('[data-bubu-upsell], .bubu-videos, [data-bubu-carril-raiz]');
  if (raiz) {
    raiz.addEventListener('click', (e) => {
      if (e.target.closest('[data-bubu-carril-prev],[data-bubu-carril-next]')) despiertaFotos(carril);
    });
  }
});

if (!matchMedia('(prefers-reduced-motion: reduce)').matches && typeof IntersectionObserver === 'function') {
  // El carril lleva scroll-snap. Al poner scrollLeft a mano el navegador lo devolvía a 0 en
  // el acto —26 px no llegan al siguiente punto de anclaje— y el aviso no se veía NUNCA,
  // ni en móvil ni en PC. Comprobado midiendo: con anclaje, scrollLeft se queda en 0; sin
  // anclaje, se queda en 26. Así que hay que soltar el anclaje mientras dura el gesto y
  // moverlo a mano; al acabar se devuelve todo como estaba. Solo se toca scrollLeft, que
  // el compositor ya sabe mover: no repinta nada ni pide layout.
  const avisa = (carril) => {
    if (carril._bubuAviso) return;
    if (carril.scrollWidth <= carril.clientWidth + 8) return;
    if (carril.scrollLeft > 2) return;
    carril._bubuAviso = true;
    const anclaje = carril.style.scrollSnapType;
    const suave = carril.style.scrollBehavior;
    const devuelve = () => {
      carril.style.scrollSnapType = anclaje;
      carril.style.scrollBehavior = suave;
    };
    carril.style.scrollSnapType = 'none';
    carril.style.scrollBehavior = 'auto';
    // Si el cliente toca el carril a media animación, mandamos nosotros a un lado: se
    // corta el aviso y se le devuelve el control sin pelear por el scroll.
    let cortado = false;
    const corta = () => { cortado = true; };
    ['pointerdown', 'touchstart', 'wheel'].forEach((ev) =>
      carril.addEventListener(ev, corta, { once: true, passive: true })
    );
    // TRES EMPUJONES, NO UNO. Un solo tirón de 26 px en 800 ms se lo pierde cualquiera
    // que en ese momento estuviera leyendo el precio: para cuando levantas la vista ya
    // ha pasado. Aquí son tres, cada vez más cortos —34, 18 y 9 px—, con una pausa
    // entre ellos para que se lean como tres golpecitos y no como una vibración. Dura
    // unos dos segundos en total: tiempo de sobra para que el ojo lo pille, y se acaba
    // solo, sin quedarse moviéndose de fondo.
    const CICLOS = [
      { tope: 34, ida: 380, vuelta: 320, pausa: 150 },
      { tope: 18, ida: 300, vuelta: 260, pausa: 150 },
      { tope: 9, ida: 240, vuelta: 220, pausa: 0 },
    ];
    const curva = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    const tramos = [];
    let reloj = 0;
    CICLOS.forEach((c) => {
      tramos.push({ a: reloj, b: reloj + c.ida, de: 0, hacia: c.tope });
      reloj += c.ida;
      tramos.push({ a: reloj, b: reloj + c.vuelta, de: c.tope, hacia: 0 });
      reloj += c.vuelta + c.pausa;
    });
    const total = reloj;
    const t0 = performance.now();
    const paso = (ahora) => {
      if (cortado) return devuelve();
      const t = ahora - t0;
      if (t >= total) {
        carril.scrollLeft = 0;
        return devuelve();
      }
      // El primer tramo que todavía no ha terminado. Si aún no ha empezado, estamos en
      // la pausa de después del empujón anterior: quieto en el sitio.
      const tr = tramos.find((x) => t < x.b);
      carril.scrollLeft = t < tr.a ? 0 : tr.de + (tr.hacia - tr.de) * curva((t - tr.a) / (tr.b - tr.a));
      requestAnimationFrame(paso);
    };
    requestAnimationFrame(paso);
  };
  const ojo = new IntersectionObserver(
    (filas) => {
      filas.forEach((f) => {
        if (!f.isIntersecting) return;
        ojo.unobserve(f.target);
        setTimeout(() => avisa(f.target), 260);
      });
    },
    { threshold: 0.4 }
  );
  document.querySelectorAll('.bubu-upsell [data-bubu-carril]').forEach((c) => ojo.observe(c));
}
